import React, { useState, useEffect } from 'react';
import CustomSelect from '@/components/common/CustomSelect';
import Button from '@/components/common/Button';
import ValidatedInput from '@/components/common/ValidatedInput';
import { useSucursal } from '@/context/SucursalContext';
import { FaUser, FaPhone, FaEnvelope, FaIdCard, FaMapMarkerAlt } from 'react-icons/fa';
import Switch from '@/components/common/Switch';
import * as mecanicosApiService from '@/services/mecanicosApiService';
import toastService from '@/utils/toastService';
import { normalizeDataObject } from '../../utils/normalizationUtils';

/**
 * Formulario para crear o editar mecánicos
 * 
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.initialData - Datos iniciales del mecánico (para edición)
 * @param {Array} props.sucursales - Lista de sucursales disponibles
 * @param {Array} props.especialidades - Lista de especialidades disponibles
 * @param {function} props.onSubmit - Función a ejecutar al enviar el formulario
 * @param {function} props.onCancel - Función a ejecutar al cancelar
 * @param {function} props.onStatusChange - Función a ejecutar cuando cambia el estado
 * @returns {JSX.Element} - Componente de formulario de mecánico
 */
const MecanicoForm = ({ 
  initialData = {}, 
  sucursales = [], 
  especialidades = [],
  onSubmit, 
  onCancel,
  onStatusChange 
}) => {
  // Obtener la sucursal seleccionada del contexto
  const { selectedSucursal, sucursalData } = useSucursal();
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    especialidad: '', // Mantener para compatibilidad
    categoria_id: '', // Nuevo campo para ID de especialidad
    sucursal_id: '',
    activo: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Si hay datos iniciales, actualizar el estado del formulario
  useEffect(() => {
    if (initialData && Object.keys(initialData).length) {
      // Normalizar datos iniciales para manejar cualquier formato
      const normalizedData = normalizeDataObject(initialData, 'mecanico');
      
      setFormData({
        nombre: normalizedData.nombre || normalizedData.Nombre || '',
        email: normalizedData.email || normalizedData.Email || '',
        telefono: normalizedData.telefono || normalizedData.Telefono || '',
        especialidad: normalizedData.especialidad || normalizedData.Especialidad || '', // Mantener para compatibilidad
        categoria_id: normalizedData.categoriaId || normalizedData.categoria_id || '', // Usar ID de especialidad si existe
        sucursal_id: normalizedData.sucursalId || normalizedData.sucursal_id || normalizedData.Sucursal || selectedSucursal || '',
        activo: normalizedData.activo !== false && normalizedData.Activo !== false // Por defecto true
      });
    } else if (selectedSucursal) {
      // Si es un nuevo mecánico, usar la sucursal seleccionada
      setFormData(prev => ({
        ...prev,
        sucursal_id: selectedSucursal
      }));
    }
  }, [initialData, selectedSucursal]);

  // Manejar cambios en el formulario
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevData => ({ 
      ...prevData, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  // Manejar toggle del estado activo/inactivo
  const handleToggleActivo = async (newActivo) => {
    // Solo permitir toggle si estamos editando (hay initialData con id)
    if (!initialData || !initialData.id) {
      return;
    }

    // Si no se pasa parámetro, usar el valor actual invertido
    if (typeof newActivo !== 'boolean') {
      newActivo = !formData.activo;
    }
    
    try {
      // Actualizar inmediatamente en la base de datos
      await mecanicosApiService.updateMecanico(initialData.id, {
        activo: newActivo
      });
      
      // Actualizar el estado local
      setFormData(prev => ({ ...prev, activo: newActivo }));
      
      // Mostrar mensaje de éxito
      toastService.success(`Mecánico ${newActivo ? 'activado' : 'desactivado'} correctamente`);
      
      // Llamar callback para refrescar la lista
      if (onStatusChange) {
        onStatusChange();
      }
      
    } catch (error) {
      console.error('Error al actualizar estado del mecánico:', error);
      toastService.error('Error al actualizar el estado del mecánico');
    }
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    // Validar que se haya seleccionado una sucursal
    if (!formData.sucursal_id) {
      alert("Por favor, selecciona una sucursal para el mecánico.");
      return;
    }

    setIsSubmitting(true);

    try {
      // Preparar datos para enviar
      const mecanicoData = {
        ...formData,
        // Asegurar que se envíe el categoria_id si está disponible
        categoria_id: formData.categoria_id || null,
        // Asegurar que experiencia sea número
        experiencia: parseInt(formData.experiencia || 0)
      };
      
      // Normalizar datos antes de enviar
      const normalizedFormData = normalizeDataObject(mecanicoData, 'mecanico');
      
      await onSubmit(normalizedFormData);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form id="mecanico-form" onSubmit={handleSubmit} className="text-white">
      
      <div className="md:grid md:grid-cols-2 md:gap-6">
        {/* Columna izquierda: información personal */}
        <div>
          <div className="bg-gray-800 p-4 rounded-lg border border-gray-600 mb-5">
            <h3 className="text-lg font-medium mb-4">Información Personal</h3>
            {/* Nombre completo */}
            <div className="mb-4">
              <label className="flex items-center text-sm font-medium mb-2">
                <FaUser className="text-gray-400 mr-2 text-xs" />
                Nombre Completo<span className="text-red-500 ml-1">*</span>
              </label>
              <ValidatedInput
                type="text"
                name="nombre"
                placeholder="Nombre y apellidos"
                value={formData.nombre}
                onChange={handleChange}
                required
                requiredMessage="El nombre es requerido"
              />
            </div>
            {/* Email */}
            <div className="mb-4">
              <label className="flex items-center text-sm font-medium mb-2">
                <FaEnvelope className="text-gray-400 mr-2 text-xs" />
                Correo Electrónico
              </label>
              <ValidatedInput
                type="email"
                name="email"
                placeholder="correo@ejemplo.com"
                value={formData.email}
                onChange={handleChange}
              />
            </div>
            {/* Teléfono */}
            <div className="mb-4">
              <label className="flex items-center text-sm font-medium mb-2">
                <FaPhone className="text-gray-400 mr-2 text-xs" />
                Teléfono
              </label>
              <ValidatedInput
                type="tel"
                name="telefono"
                placeholder="+56 9 XXXX XXXX"
                value={formData.telefono}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>
        {/* Columna derecha: información profesional */}
        <div>
          <div className="bg-gray-800 p-4 rounded-lg border border-gray-600 mb-5">
            <h3 className="text-lg font-medium mb-4">Información Profesional</h3>
            {/* Sucursal oculta */}
            <input 
              type="hidden" 
              name="sucursal_id" 
              value={formData.sucursal_id || selectedSucursal || ''} 
            />
            {/* Especialidad */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Especialidad
              </label>
              <CustomSelect
                name="categoria_id"
                value={formData.categoria_id}
                onChange={handleChange}
                options={especialidades
                  .sort((a, b) => a.nombre.localeCompare(b.nombre))
                  .map(especialidad => ({
                    id: especialidad.id,
                    value: especialidad.id,
                    label: especialidad.nombre
                  }))}
                placeholder="Seleccionar especialidad"
                className="w-full"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Botones de acción */}
      <div className="border-t border-gray-700 pt-4 mt-6">
        <div className="flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center">
          {/* Switch de estado activo */}
          <div className="order-1 sm:order-1">
            <Switch
              checked={formData.activo}
              onChange={handleToggleActivo}
              activeLabel="Mecánico activo"
              inactiveLabel="Mecánico inactivo"
              size="md"
            />
          </div>
          
          {/* Botones */}
          <div className="flex flex-col sm:flex-row gap-3 order-2 sm:order-2 md:justify-end">
            <Button 
              variant="secondary" 
              onClick={onCancel}
              className="w-full sm:w-auto py-2.5 px-4 order-1"
            >
              Cancelar
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitting}
              className="w-full sm:w-auto py-2.5 px-4 order-2"
            >
              {initialData.id ? 'Actualizar' : 'Guardar'} Mecánico
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default MecanicoForm;